import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { LOGO_URL } from "../lib/constants";

const inputCls =
  "w-full bg-[#0A0A0A] border border-white/15 focus:border-[#E60000] outline-none text-white text-sm px-5 py-4 placeholder:text-white/30 transition-colors duration-300";

export default function AdminLogin() {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/login", { phone, password });
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grid grid-cols-1 lg:grid-cols-2" data-testid="admin-login-page">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A0A0A] border-r border-white/10 relative overflow-hidden">
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#E60000]/10 blur-[140px]" />
        <Link to="/" data-testid="admin-login-logo-link">
          <img src={LOGO_URL} alt="Diggy Shop" className="h-16 w-auto" />
        </Link>
        <div className="relative z-10">
          <p className="text-xs tracking-[0.3em] font-bold text-[#E60000] mb-4">ÁREA ADMINISTRATIVA</p>
          <h1 className="font-display font-black text-5xl text-white leading-none">
            Gestão<br /><span className="italic text-[#E60000]">Diggy Shop</span>
          </h1>
          <p className="mt-6 text-sm text-white/50 font-light max-w-sm">
            Acompanhe os pedidos de orçamento recebidos pelo site em tempo real.
          </p>
        </div>
        <p className="text-xs text-white/30 relative z-10">Desde 1998 • Jundiaí/SP</p>
      </div>

      <div className="flex items-center justify-center bg-[#171717] px-6 py-16">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
          data-testid="admin-login-form"
        >
          <Link to="/" className="lg:hidden block mb-10" data-testid="admin-login-logo-link-mobile">
            <img src={LOGO_URL} alt="Diggy Shop" className="h-14 w-auto" />
          </Link>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#E60000] flex items-center justify-center">
              <Lock size={16} className="text-white" />
            </div>
            <h2 className="text-xl font-extrabold text-white tracking-tight">Acesso restrito</h2>
          </div>

          <div className="space-y-5">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Telefone fixo"
              required
              className={inputCls}
              data-testid="admin-phone-input"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Senha"
              required
              className={inputCls}
              data-testid="admin-password-input"
            />
          </div>

          {error && (
            <p className="mt-4 text-sm text-[#E60000] font-semibold" data-testid="admin-login-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="btn-sharp mt-8 w-full flex items-center justify-center gap-3 bg-[#E60000] hover:bg-white hover:text-[#0A0A0A] disabled:opacity-60 text-white text-sm tracking-[0.2em] font-extrabold px-10 py-5 transition-colors duration-300"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : null}
            {loading ? "ENTRANDO..." : "ENTRAR"}
          </button>

          <Link to="/" data-testid="admin-back-to-site-link" className="block mt-6 text-center text-xs tracking-[0.15em] text-white/40 hover:text-[#E60000] transition-colors duration-300">
            ← VOLTAR AO SITE
          </Link>
        </motion.form>
      </div>
    </div>
  );
}
